import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";

export interface AppCountry {
  id: string;
  name: string;
  phone: string;
  slug: string;
}

export interface AppCampus {
  id: string;
  name: string;
  slug: string;
}

export type AppStudentCampus =
  | string
  | number
  | AppCampus
  | { id?: string | number; name?: string; slug?: string }
  | null;

export interface AppStudent {
  first_name: string;
  last_name: string;
  email: string;
  sid: string;
  id: number;
  secret: string;
  campus?: AppStudentCampus;
  country: string;
  phone: string;
}

export interface AppMessage {
  open: boolean;
  severity?: "error" | "warning" | "info" | "success";
  message?: string;
}

export interface AppState {
  student?: AppStudent;
  token?: string;
  isLoggedIn: boolean;
  countries: AppCountry[];
  campuses: AppCampus[];
  backdrop: boolean;
  message: AppMessage;
}

const initialState: AppState = {
  student: undefined,
  token: undefined,
  isLoggedIn: false,
  countries: [],
  campuses: [],
  backdrop: false,
  message: { open: false, severity: "info" },
};

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    login: (state, action: PayloadAction<AppStudent>) => {
      return {
        ...state,
        student: action.payload,
      };
    },
    auth: (
      state,
      action: PayloadAction<{ student: AppStudent; token: string }>
    ) => {
      const student = {
        ...action.payload.student,
        campus: action.payload.student.campus ?? state.student?.campus,
      };

      return {
        ...state,
        student: student,
        isLoggedIn: true,
        token: action.payload.token,
      };
    },
    logout: (state) => {
      return { ...state, isLoggedIn: false, token: undefined };
    },
    country: (state, action: PayloadAction<AppCountry[]>) => {
      return {
        ...state,
        countries: action.payload,
      };
    },
    campus: (state, action: PayloadAction<AppCampus[]>) => {
      return {
        ...state,
        campuses: action.payload,
      };
    },
    backdrop: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        backdrop: action.payload,
      };
    },
    message: (state, action: PayloadAction<AppMessage>) => {
      return {
        ...state,
        message: action.payload,
      };
    },
    clear: (state) => {
      return { ...initialState };
    },
    setSid: (state, action: PayloadAction<string>) => {
      const student = state.student;
      if (state.student) {
        state.student.sid = action.payload;
        return {
          ...state,
          student: student,
        };
      }
      return state;
    },
  },
});

export const {
  login,
  logout,
  country,
  auth,
  backdrop,
  campus,
  message,
  clear,
  setSid,
} = appSlice.actions;
export const getApp = (state: RootState) => state.app;
export default appSlice.reducer;
